import React, { useRef, useState } from "react";
import classNames from "classnames";
import WidgetCard from "../_shared/WidgetCard";
import ViewAllButton from "../_shared/ViewAllButton";
import ViewAllModal from "../modals/ViewAllModal";
import type { ViewAllPage } from "../modals/ViewAllModal";
import { TrendArrow } from "../_shared/icons";
import {
  AVATAR_TINTS,
  CampusDatum,
  VIEW_ALL_PAGE_SIZE,
  fmt,
  pct,
} from "../data";
import type { BlurTarget } from "../_shared/exportPdf";
import styles from "./style.module.scss";

interface ReaderCampusesProps {
  campuses: CampusDatum[];
  maxBooks: number;
  /** `rank` is 1-based across pages — only rank 1 earns a certificate. */
  onOpenCampus: (campus: CampusDatum, rank: number) => void;
  /** header overrides for campus-scoped reuse ("The Reader Classes") */
  title?: string;
  subtitle?: string;
  /** column header for the first entity column (default "Campus") */
  entityLabel?: string;
  /** override the row meta line (default "N classes · M students") */
  metaFor?: (campus: CampusDatum) => React.ReactNode;
  /** what the export may blur the row name / meta line as (default the name
   *  is a campus and the meta line nothing) */
  nameBlur?: BlurTarget;
  metaBlur?: BlurTarget;
  /** render a second thin bar under the books bar + a legend */
  secondaryBar?: boolean;
  /** legend/label for the secondary bar (default "Adoption") */
  secondaryLabel?: string;
  /** width source for the secondary bar 0–100 (default the row's adoption %) */
  secondaryValue?: (campus: CampusDatum) => number;
  /** show the trailing Trend column (default true; campus has no per-class trend) */
  showTrend?: boolean;
  /** show a dedicated Students count column */
  showStudents?: boolean;
  /** when given, the header gets a "View All" button opening a paginated modal */
  fetchPage?: (page: number) => Promise<ViewAllPage>;
  /** copy for that modal */
  viewAllTitle?: string;
  viewAllSubtitle?: string;
}

/** Campuses ranked by reading performance — a row opens the drill-down modal. */
export default function ReaderCampuses({
  campuses,
  maxBooks,
  onOpenCampus,
  title = "The Reader Campuses",
  subtitle = "Ranked by reading performance — tap a row to drill in",
  entityLabel = "Campus",
  metaFor,
  nameBlur = "campus",
  metaBlur,
  secondaryBar = false,
  secondaryLabel = "Adoption",
  secondaryValue,
  showTrend = true,
  showStudents = false,
  fetchPage,
  viewAllTitle,
  viewAllSubtitle,
}: ReaderCampusesProps) {
  const [open, setOpen] = useState(false);
  const pageRef = useRef(1); // page rendered in the modal (drives the rank offset)

  const legend = secondaryBar ? (
    <div className={styles.legend}>
      <span className={styles.legendItem}>
        <span className={styles.legendDot} />
        Books read
      </span>
      <span className={styles.legendItem}>
        <span className={classNames(styles.legendDot, styles.legendDotSoft)} />
        {secondaryLabel}
      </span>
    </div>
  ) : undefined;

  /* header holds the legend and, when the widget is paginated, "View All" */
  const headerRight =
    legend || fetchPage ? (
      <div className={styles.headActions}>
        {fetchPage && <ViewAllButton onClick={() => setOpen(true)} />}
        {legend}
      </div>
    ) : undefined;

  /** the same table markup drives the card and the modal (offset = rank base) */
  const renderTable = (list: CampusDatum[], offset = 0, max = maxBooks) => (
    <div className={styles.scrollWrap} data-ho-scroll>
        <div
          className={classNames(
            styles.table,
            !showTrend && styles.noTrend,
            showStudents && styles.withStudents,
          )}
        >
          <div className={styles.headRow}>
            <div>#</div>
            <div>{entityLabel}</div>
            {showStudents && <div className={styles.right}>Students</div>}
            <div className={styles.right}>Books read</div>
            <div className={styles.volCol}>Reading volume</div>
            {showTrend && <div className={styles.right}>Trend</div>}
          </div>
          {list.map((c, i) => {
            const tint = AVATAR_TINTS[(offset + i) % AVATAR_TINTS.length];
            const up = c.trend > 0;
            return (
              <div
                key={`${c.name}-${offset + i}`}
                className={styles.row}
                role="button"
                onClick={() => onOpenCampus(c, offset + i + 1)}
              >
            <div>
              <span className={styles.rank}>{offset + i + 1}</span>
            </div>
            <div className={styles.campusCell}>
              <span
                className={styles.avatar}
                style={{ background: tint.tint, color: tint.color }}
                data-ho-blur={nameBlur}
              >
                {c.initials}
              </span>
              <div className={styles.campusText}>
                <div className={styles.campusName} data-ho-blur={nameBlur}>
                  {c.name}
                </div>
                <div className={styles.campusMeta} data-ho-blur={metaBlur}>
                  {metaFor
                    ? metaFor(c)
                    : `${c.classes} classrooms · ${fmt(c.students)} students`}
                </div>
              </div>
            </div>
            {showStudents && (
              <div className={styles.students}>{fmt(c.students)}</div>
            )}
            <div className={styles.books}>{fmt(c.books)}</div>
            <div className={styles.volCol}>
              <div className={styles.track}>
                <div
                  className={styles.fill}
                  style={{ width: pct(c.books, max) }}
                />
              </div>
              {secondaryBar && (
                <div className={styles.trackSm}>
                  <div
                    className={styles.fillSoft}
                    style={{
                      width: `${secondaryValue ? secondaryValue(c) : c.adoption}%`,
                    }}
                  />
                </div>
              )}
            </div>
                {showTrend && (
                  <div className={styles.trend}>
                    <TrendArrow up={up} />
                    <span style={{ color: up ? "#027A48" : "#B42318" }}>
                      {(up ? "+" : "") + c.trend}%
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
  );

  return (
    <WidgetCard title={title} subtitle={subtitle} right={headerRight} flush>
      {renderTable(campuses)}

      {open && fetchPage && (
        <ViewAllModal
          title={viewAllTitle ?? title}
          subtitle={viewAllSubtitle ?? subtitle}
          onClose={() => setOpen(false)}
          fetchPage={(page) => {
            pageRef.current = page;
            return fetchPage(page);
          }}
          renderContent={(rows) => {
            const list = rows as CampusDatum[];
            // scale bars against the page's own top row so they stay readable
            const max = Math.max(1, ...list.map((c) => c.books));
            return renderTable(
              list,
              (pageRef.current - 1) * VIEW_ALL_PAGE_SIZE,
              max,
            );
          }}
        />
      )}
    </WidgetCard>
  );
}
